import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { 
  LogOut, LayoutDashboard, MessageSquare, FileText, Settings, UserCheck, 
  ExternalLink, ShieldCheck, Menu, X, ChevronDown, ChevronRight, Users, 
  Building2, Send, Contact, Clock, Zap, Bot, TrendingUp, User, Phone,Tag,
  Crown, Building, BarChart3, Activity, Calendar, Award, CreditCard, Lock,
  FileSignature, Megaphone, Plug, Database
} from 'lucide-react';
import axios from 'axios';
import NotificationBell from './NotificationBell';
import api from '../services/api';
import ThemeToggle from './ThemeToggle';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Layout = ({ children }) => {
  // ⭐ MOVER PARA DENTRO DO COMPONENTE
  const { t, language } = useLanguage();
  
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 1500); // ⭐ NOVO: menu lateral retrátil

  // ⭐ NOVO: encolhe/expande o menu sozinho conforme o tamanho da tela (notebooks
  // menores, tipo 1366x768, ganham mais espaço pro conteúdo automaticamente)
  useEffect(() => {
    const handleResize = () => setCollapsed(window.innerWidth < 1500);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [openGroups, setOpenGroups] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [tenantPlan, setTenantPlan] = useState(null);
  const [allowedPages, setAllowedPages] = useState([]);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [deniedPage, setDeniedPage] = useState('');
  
  const [userAvatar, setUserAvatar] = useState(null);

  useEffect(() => {
    const loadUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const res = await api.get('/api/profile/me');
        const userData = res.data.user;
        
                setCurrentUser({
          id: payload.id,
          name: userData.name || payload.name,
          email: userData.email || payload.email,
          role: userData.role || payload.role,
          sector_id: userData.sector_id || payload.sector_id,
          sector_name: userData.sector_name || payload.sector_name,
          user_type: userData.user_type || payload.user_type,
          tenant_id: userData.tenant_id || payload.tenant_id,
          tenant_name: userData.tenant_name || payload.tenant_name,
          avatar_url: userData.avatar_url,
          can_access_envios: userData.can_access_envios !== false
        });

        // ⭐ NOVO: mantém a preferência de som de notificação sempre atualizada
        localStorage.setItem('notification_sound_enabled', userData.notification_sound_enabled !== false ? 'true' : 'false');

                if (userData.avatar_url) {
          setUserAvatar(`${API}${userData.avatar_url}`);
        } else {
          setUserAvatar(null); // ⭐ CORRIGIDO: limpa a foto antiga se ela foi removida
        }
      } catch (err) {
        console.error('Erro ao carregar dados do usuário:', err);
      }
    };
    loadUserData();

    // ⭐ NOVO: recarrega o perfil (nome, foto, etc) sempre que a tela de Perfil avisar
    window.addEventListener('profile-updated', loadUserData);
    return () => window.removeEventListener('profile-updated', loadUserData);
  }, []);

  useEffect(() => {
    if (currentUser && currentUser.tenant_id) {
      loadTenantPlan();
    }
  }, [currentUser]);

  const loadTenantPlan = async () => {
    try {
      const res = await api.get('/api/plans/my-plan');
      setTenantPlan(res.data);
      setAllowedPages(res.data.allowed_pages || []);
    } catch (err) {
      console.error('Erro ao carregar plano:', err);
      setAllowedPages(['*']);
    }
  };

  const toggleGroup = (label) => {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isPageAllowed = (path) => {
    if (!currentUser) return false;
    if (currentUser.role === 'master_admin') return true;
    
    const cleanPath = path.split('?')[0].replace(/^\//, '');

    // ⭐ NOVO: permissão individual de "Envios" (assinatura digital), controlada
    // pelo admin/gerente na tela de Usuários — vale além do plano da empresa
    if (cleanPath === 'envios' && currentUser.can_access_envios === false) {
      return false;
    }
    
    const alwaysAllowed = [
      '',
      'profile',
      'dashboard',
      'template-variables',
      'conversations',
      'my-conversations',
      'contacts',
      'appointments',
      'achievements',
      'attendee-dashboard',
    ];
    
    if (alwaysAllowed.includes(cleanPath)) {
      return true;
    }
    
    if (allowedPages.length === 0 || allowedPages.includes('*')) {
      return true;
    }
    
    return allowedPages.includes(cleanPath);
  };

  const handleMenuClick = (path, e) => {
    if (!isPageAllowed(path)) {
      e.preventDefault();
      setDeniedPage(path);
      setShowAccessDenied(true);
      return false;
    }
    return true;
  };

  // ⭐ FUNÇÃO COM TRADUÇÕES
  const getMenuItems = () => {
    const role = currentUser?.role;
    const isMasterAdmin = role === 'master_admin';
    const isTenantAdmin = role === 'tenant_admin';
    const isManager = role === 'manager';
    const isAttendant = role === 'attendant';

    if (isMasterAdmin) {
      return [
        { path: '/master-admin/dashboard', icon: LayoutDashboard, label: t('layout.masterDashboard') },
        {
          type: 'group',
          label: t('layout.companies'),
          icon: Building2,
          children: [
            { path: '/master-admin/tenants', icon: Building2, label: t('layout.allCompanies') },
            { path: '/master-admin/reports', icon: BarChart3, label: t('layout.reports') },
          ],
        },
        {
          type: 'group',
          label: t('layout.system'),
          icon: Settings,
          children: [
            { path: '/master-admin/users', icon: Users, label: t('layout.systemUsers') },
            { path: '/master-admin/audit-logs', icon: Activity, label: t('layout.auditLogs') },
            { path: '/master-admin/settings', icon: Settings, label: t('layout.settings') },
          ],
        },
        { path: '/profile', icon: User, label: t('layout.profile') },
      ];
    }

        if (isTenantAdmin || isManager) {
      return [
        { path: '/', icon: LayoutDashboard, label: t('layout.dashboard') },
        {
          type: 'group',
          label: t('layout.attendance'),
          icon: MessageSquare,
          children: [
            { path: '/conversations', icon: MessageSquare, label: t('layout.conversations') },
            { path: '/contacts', icon: Contact, label: t('layout.contacts') },
            { path: '/funnel-board', icon: TrendingUp, label: t('layout.visualFunnel') },
            { path: '/envios', icon: FileSignature, label: 'Envios' },
          ],
        },
        {
          type: 'group',
          label: t('layout.crm'),
          icon: Users,
          children: [
            { path: '/leads', icon: Users, label: t('layout.leads') },
          ],
        },
        {
          type: 'group',
          label: t('layout.monitoring'),
          icon: BarChart3,
          children: [
            { path: '/team-performance', icon: Users, label: t('layout.teamPerformance') },
            { path: '/appointments', icon: Calendar, label: t('layout.appointments') },
            { path: '/achievements', icon: Award, label: t('layout.achievements') },
          ],
        },
        {
          type: 'group',
          label: t('layout.administration'),
          icon: Building2,
          children: [
            { path: '/users', icon: Users, label: t('layout.users') },
            { path: '/sectors', icon: Building2, label: t('layout.sectors') },
            { path: '/phone-numbers', icon: Phone, label: t('layout.phoneNumbers') },
          ],
        },
        {
          type: 'group',
          label: 'Marketing',
          icon: Megaphone,
          children: [
            { path: '/templates', icon: FileText, label: t('layout.templates') },
            { path: '/bulk-messaging', icon: Send, label: t('layout.bulkMessaging') },
            { path: '/campaigns', icon: BarChart3, label: 'Campanhas' },
            { path: '/base-dados', icon: Database, label: 'Base de Dados' },
          ],
        },
        {
          type: 'group',
          label: 'Integrações',
          icon: Plug,
          children: [
            { path: '/bi-integration', icon: BarChart3, label: t('layout.biIntegration') },
            { path: '/chatbots', icon: Bot, label: t('layout.chatbot') },
            { path: '/automations', icon: Zap, label: t('layout.automations') },
            { path: '/ai-agents', icon: Bot, label: t('layout.aiAgents') },
          ],
        },
        { path: '/reports', icon: TrendingUp, label: t('layout.reports') },
        { path: '/opt-in', icon: UserCheck, label: t('layout.optIn') },
        { path: '/business-accounts', icon: Building2, label: t('layout.businessAccounts') },
                { path: '/settings', icon: Settings, label: 'Configurações' },
        { path: '/profile', icon: User, label: t('layout.profile') },
      ];
    }

    if (isAttendant) {
      return [
        { path: '/', icon: LayoutDashboard, label: t('layout.myDashboard') },
        { path: '/my-conversations', icon: MessageSquare, label: t('layout.myConversations') },
        { path: '/contacts', icon: Contact, label: t('layout.contacts') },
        { path: '/envios', icon: FileSignature, label: 'Envios' },
        { path: '/profile', icon: User, label: t('layout.profile') },
        { path: '/attendee-dashboard', icon: LayoutDashboard, label: t('layout.myPerformance') },
        { path: '/appointments', icon: Calendar, label: t('layout.appointments') },
        { path: '/achievements', icon: Award, label: t('layout.achievements') },
      ];
    }

    return [
      { path: '/', icon: LayoutDashboard, label: t('layout.dashboard') },
      {
        type: 'group',
        label: t('layout.attendance'),
        icon: MessageSquare,
        children: [
          { path: '/conversations', icon: MessageSquare, label: t('layout.conversations') },
          { path: '/contacts', icon: Contact, label: t('layout.contacts') },
          { path: '/tasks', icon: Clock, label: t('layout.tasks') },
        ],
      },
      { path: '/profile', icon: User, label: t('layout.profile') },
    ];
  };

  const menuItems = getMenuItems();
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Ignora erro de rede no logout
    } catch (err) {
      console.error('Erro no logout:', err);
    } finally {
      // 🛑 LIMPEZA NUCLEAR: Remove TUDO do armazenamento
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirecionamento seguro (replace impede o botão "Voltar" do navegador)
      window.location.replace('/login');
    }
  };
  const roleLabels = { 
    master_admin: t('role.master_admin'),
    admin: t('role.admin'), 
    tenant_admin: t('role.tenant_admin'),
    manager: t('role.manager'), 
    attendant: t('role.attendant') 
  };
  
  const roleColors = { 
    master_admin: 'bg-gradient-to-br from-yellow-500 to-orange-600', 
    admin: 'bg-purple-500',
    tenant_admin: 'bg-blue-500',
    manager: 'bg-blue-500', 
    attendant: 'bg-green-500' 
  };

  const roleBadgeColors = {
    master_admin: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    admin: 'bg-purple-100 text-purple-700',
    tenant_admin: 'bg-blue-100 text-blue-700',
    manager: 'bg-blue-100 text-blue-700',
    attendant: 'bg-green-100 text-green-700'
  };

  return (
    <div className="flex h-screen bg-gray-50 font-sans text-gray-900 dark:bg-slate-900 dark:text-slate-100">
      {/* Sidebar Desktop */}
      <aside className={`hidden md:flex flex-col ${collapsed ? 'w-20' : 'w-64'} bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 shadow-sm transition-all duration-200`}>
        <div className="p-4 flex items-center gap-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <Menu size={20} />
          </button>
          {!collapsed && (
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0 ${
              currentUser?.role === 'master_admin' 
                ? 'bg-gradient-to-br from-yellow-500 to-orange-600 shadow-orange-200'
                : 'bg-green-600 shadow-green-200'
            }`}>
              {currentUser?.role === 'master_admin' ? <Crown size={24} /> : <MessageSquare size={24} />}
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0">
              <h1 className={`text-xl font-bold bg-clip-text text-transparent truncate ${
                currentUser?.role === 'master_admin'
                  ? 'bg-gradient-to-r from-yellow-600 to-orange-700'
                  : 'bg-gradient-to-r from-green-600 to-green-800'
              }`}>
                Soulidari
              </h1>
              {currentUser?.tenant_name && (
                <p className="text-xs text-gray-500 truncate">{currentUser.tenant_name}</p>
              )}
            </div>
          )}
        </div>

        {currentUser && (
          <div className={`${collapsed ? 'px-2' : 'px-4'} pb-4`}>
            <div className={`${collapsed ? 'p-2 justify-center' : 'p-3'} rounded-xl flex items-center gap-3 ${
              currentUser.role === 'master_admin' 
                ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200'
                : 'bg-gray-50'
            }`}>
              <div
                className={`w-10 h-10 ${roleColors[currentUser.role]} rounded-full flex items-center justify-center text-white font-bold shadow-sm overflow-hidden flex-shrink-0`}
                title={collapsed ? currentUser.name : undefined}
              >
                {userAvatar ? (
                  <img 
                    src={userAvatar} 
                    alt={currentUser.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  currentUser.name.charAt(0).toUpperCase()
                )}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{currentUser.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {roleLabels[currentUser.role]}
                    {currentUser.sector_name && ` • ${currentUser.sector_name}`}
                  </p>
                </div>
              )}
            </div>
            
            {!collapsed && tenantPlan && currentUser.role !== 'master_admin' && (
              <div className="mt-2 px-3 py-2 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-1.5">
                  <CreditCard size={12} className="text-orange-600" />
                  <p className="text-[10px] font-bold text-orange-700 uppercase">
                    {tenantPlan.name || tenantPlan.plan_name}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-4'} space-y-1 mt-2 overflow-y-auto`}>
          {menuItems.map((item, idx) => {
            if (item.type === 'group') {
              // ⭐ CORRIGIDO: antes todo grupo começava aberto por padrão — agora
              // começa fechado, só abre se a pessoa clicar (vale pra todos os grupos)
              const isOpen = openGroups[item.label] === true;
              return (
                <div key={idx}>
                  {!collapsed && (
                    <button
                      onClick={() => toggleGroup(item.label)}
                      className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={20} />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  )}
                  {(collapsed || isOpen) && (
                    <div className={collapsed ? 'space-y-1' : 'ml-6 space-y-1 mt-1'}>
                      {item.children.map((child) => {
                        const Icon = child.icon;
                        const allowed = isPageAllowed(child.path);
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={(e) => !allowed && handleMenuClick(child.path, e)}
                            title={collapsed ? child.label : undefined}
                            className={`flex items-center ${collapsed ? 'justify-center px-4 py-3' : 'gap-3 px-4 py-2'} rounded-xl transition-all ${
                              !allowed
                                ? 'text-gray-400 opacity-60 cursor-not-allowed'
                                : isActive(child.path)
                                ? 'bg-green-50 text-green-700 font-semibold'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                          >
                            <Icon size={collapsed ? 20 : 18} />
                            {!collapsed && <span className="flex-1">{child.label}</span>}
                            {!collapsed && !allowed && <Lock size={12} className="text-gray-400" />}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const Icon = item.icon;
            const allowed = isPageAllowed(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => !allowed && handleMenuClick(item.path, e)}
                title={collapsed ? item.label : undefined}
                className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-4 py-3 rounded-xl transition-all ${
                  !allowed
                    ? 'text-gray-400 dark:text-slate-600 opacity-60 cursor-not-allowed'
                    : isActive(item.path)
                    ? currentUser?.role === 'master_admin'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 text-orange-700 dark:text-orange-400 font-semibold border border-yellow-200 dark:border-yellow-800'
                      : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-semibold'
                    : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-slate-200'
                }`}
              >
                <Icon size={20} />
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {!collapsed && !allowed && <Lock size={14} className="text-gray-400" />}
              </Link>
            );
          })}
        </nav>

        <div className={`${collapsed ? 'p-2' : 'p-4'} border-t border-gray-100`}>
          {currentUser?.role === 'master_admin' ? (
            <div className={`bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl ${collapsed ? 'p-2 flex justify-center' : 'p-4'} text-white shadow-lg`}>
              {collapsed ? (
                <Crown size={18} />
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <Crown size={16} />
                    <p className="text-xs font-bold uppercase tracking-wider">Master Admin</p>
                  </div>
                  <p className="text-sm opacity-90">{t('layout.fullControl')}</p>
                </>
              )}
            </div>
          ) : (
            <div className={`bg-gradient-to-br from-green-600 to-green-700 rounded-2xl ${collapsed ? 'p-2 flex justify-center' : 'p-4'} text-white shadow-lg`}>
              {collapsed ? (
                <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
              ) : (
                <>
                  <p className="text-xs font-medium opacity-80 uppercase tracking-wider">{t('layout.status')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <p className="text-sm font-semibold">{t('layout.online')}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </aside>                

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${
            currentUser?.role === 'master_admin' 
              ? 'bg-gradient-to-br from-yellow-500 to-orange-600'
              : 'bg-green-600'
          }`}>
            {currentUser?.role === 'master_admin' ? <Crown size={18} /> : <MessageSquare size={18} />}
          </div>
          <span className="font-bold text-lg">Soulidari</span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-gray-600">
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="w-64 h-full bg-white p-6 pt-20" onClick={e => e.stopPropagation()}>
            {currentUser && (
              <div className="mb-4 p-3 bg-gray-50 rounded-xl flex items-center gap-3">
                <div className={`w-10 h-10 ${roleColors[currentUser.role]} rounded-full flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0`}>
                  {userAvatar ? (
                    <img 
                      src={userAvatar} 
                      alt={currentUser.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    currentUser.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{currentUser.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {roleLabels[currentUser.role]}
                  </p>
                </div>
              </div>
            )}
            <nav className="space-y-2">
              {menuItems.map((item) => {
                if (item.type === 'group') {
                  return item.children?.map((child) => {
                    const Icon = child.icon;
                    const allowed = isPageAllowed(child.path);
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={(e) => {
                          if (!allowed) {
                            e.preventDefault();
                            handleMenuClick(child.path, e);
                          } else {
                            setIsMobileMenuOpen(false);
                          }
                        }}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                          !allowed
                            ? 'text-gray-400 opacity-60'
                            : isActive(child.path) 
                            ? 'bg-green-50 text-green-700 font-semibold' 
                            : 'text-gray-500'
                        }`}
                      >
                        <Icon size={20} />
                        <span className="flex-1">{child.label}</span>
                        {!allowed && <Lock size={14} />}
                      </Link>
                    );
                  });
                }
                const Icon = item.icon;
                const allowed = isPageAllowed(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={(e) => {
                      if (!allowed) {
                        e.preventDefault();
                        handleMenuClick(item.path, e);
                      } else {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                      !allowed
                        ? 'text-gray-400 opacity-60'
                        : isActive(item.path) 
                        ? 'bg-green-50 text-green-700 font-semibold' 
                        : 'text-gray-500'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="flex-1">{item.label}</span>
                    {!allowed && <Lock size={14} />}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pt-16 md:pt-0">
        {/* ⭐ NOVO: pro atendente, esconde essa barra nas telas de chat (Conversas
            e Minhas Conversas) — ganha altura extra pra conversa, já que ele não
            usa tema/notificação/sair com tanta frequência ali */}
        {!(currentUser?.role === 'attendant' && (location.pathname.startsWith('/conversations') || location.pathname.startsWith('/my-conversations'))) && (
        <header className="hidden md:flex h-16 items-center justify-between px-8 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">
            {menuItems
              .flatMap(i => (i.children ? i.children : i))
              .find(i => isActive(i.path))?.label || 'Soulidari App'}
          </h2>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            
            {currentUser && (
              <div className="flex flex-col items-end">
                <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{currentUser.name}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadgeColors[currentUser.role] || 'bg-gray-100 text-gray-700'}`}>
                    {roleLabels[currentUser.role]}
                  </span>
                  {currentUser.tenant_name && (
                    <span className="text-xs text-gray-500 dark:text-slate-400">• {currentUser.tenant_name}</span>
                  )}
                </div>
              </div>
            )}
            <div className={`w-10 h-10 rounded-full border-2 border-white shadow-sm overflow-hidden flex items-center justify-center text-white font-bold ${!userAvatar ? (roleColors[currentUser?.role] || 'bg-gray-200') : ''}`}>
              {userAvatar ? (
                <img 
                  src={userAvatar} 
                  alt={currentUser?.name || 'User'} 
                  className="w-full h-full object-cover"
                />
              ) : (
                currentUser?.name?.charAt(0).toUpperCase() || 'U'
              )}
            </div>
            <NotificationBell />
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-500 hover:text-red-500 transition-colors"
              title={t('layout.logout')}
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>
        )}

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {(() => {
                        const fullWidthPages = [
              '/conversations',
              '/my-conversations',
              '/leads',
              '/team-performance',
              '/users',
              '/sectors',
              '/appointments',
              '/bulk-messaging'
            ];
            const currentPath = location.pathname;
            const isFullWidth = fullWidthPages.some(page => currentPath.startsWith(page));
            
            if (isFullWidth) {
              return (
                <div className="w-full h-full">
                  {children || <Outlet />}
                </div>
              );
            } else {
              return (
                <div className="max-w-6xl mx-auto">
                  {children || <Outlet />}
                </div>
              );
            }
          })()}
        </div>
      </main>

      {/* Modal de Acesso Negado */}
      {showAccessDenied && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <Lock size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t('layout.accessRestricted')}</h3>
                <p className="text-sm text-gray-500">{t('layout.featureNotAvailable')}</p>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-orange-900">
                <strong>{t('layout.currentPlan')}:</strong> {tenantPlan?.name || 'Básico'}
              </p>
              <p className="text-xs text-orange-700 mt-1">
                {t('layout.upgradeToUnlock')}
              </p>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {t('layout.contactSupport')}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAccessDenied(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('common.close')}
              </button>
              <button
                onClick={() => {
                  setShowAccessDenied(false);
                  window.location.href = '/profile';
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-bold hover:from-orange-600 hover:to-orange-700"
              >
                {t('layout.viewPlans')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
